import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Pagination } from ".";

type PaginationSimpleProps = React.ComponentProps<"nav"> & {
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  disabled?: boolean;
  hideLabel?: boolean;
};

function PaginationSimple({
  className,
  page,
  totalPages,
  onPageChange,
  disabled = false,
  hideLabel = false,
  ...props
}: PaginationSimpleProps) {
  const isControlled = page !== undefined && totalPages !== undefined && totalPages > 0 && onPageChange;
  const currentPage = isControlled ? Math.min(Math.max(1, page), totalPages) : 1;
  const [inputValue, setInputValue] = React.useState(String(currentPage));

  React.useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw === "") {
      setInputValue("");
      return;
    }
    setInputValue(raw);
  };

  const commitPage = React.useCallback(() => {
    const parsed = parseInt(inputValue, 10);
    if (!isControlled || Number.isNaN(parsed)) {
      setInputValue(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(1, parsed), totalPages!);
    setInputValue(String(clamped));
    if (clamped !== currentPage) {
      onPageChange!(clamped);
    }
  }, [inputValue, currentPage, totalPages, isControlled, onPageChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitPage();
    }
  };

  if (!isControlled) {
    return (
      <nav
        role="navigation"
        aria-label="pagination"
        data-slot="pagination-simple"
        className={cn("mx-auto flex w-full justify-center", className)}
        {...props}
      />
    );
  }

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination-simple"
      className={cn("mx-auto flex justify-center items-center gap-2", className)}
      {...props}
    >
      <Pagination.Content>
        <Pagination.Item>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Go to previous page"
            disabled={disabled || currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            className={cn((disabled || currentPage <= 1) && "pointer-events-none opacity-50")}
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
        </Pagination.Item>
        <div className="flex items-center gap-1.5">
          {!hideLabel && <span className="text-sm text-muted-foreground whitespace-nowrap">Page</span>}
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValue}
            onChange={handlePageInputChange}
            onBlur={commitPage}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-label="Current page"
            className="w-12 h-8 text-center text-sm"
          />
          <span className="text-sm text-muted-foreground whitespace-nowrap">of {totalPages}</span>
        </div>
        <Pagination.Item>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Go to next page"
            disabled={disabled || currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className={cn((disabled || currentPage >= totalPages) && "pointer-events-none opacity-50")}
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </Pagination.Item>
      </Pagination.Content>
    </nav>
  );
}

PaginationSimple.displayName = "PaginationSimple";

export { PaginationSimple };
